import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import DashboardCard from './DashboardCard';
import { Legend, LegendItem, Footnote } from '../styles';
import { TYPE_FOLD_COLOR } from '../constants';
import RowAvatar from './RowAvatar';
import { initialsOf } from './TodayOnLeaveCard';

const DAY_MS = 24 * 60 * 60 * 1000;

const BandHeader = styled.div`
  display: flex;
  margin-left: 116px;
  border-bottom: 1px solid #F0F2F5;
  padding-bottom: 4px;
`;

const BandLabel = styled.div`
  flex: 1;
  font-size: 10px;
  font-weight: 700;
  color: #9CA3AF;
  text-align: center;
`;

const Rows = styled.div`
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 4px; }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0;
`;

const RowLabel = styled.div`
  width: 116px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Track = styled.div`
  position: relative;
  flex: 1;
  height: 20px;
  border-radius: 4px;
  background: #F7F9FB;
  background-image: ${({ $bands }) => `repeating-linear-gradient(to right,
    transparent 0 calc(${100 / $bands}% - 1px), #EDF0F4 calc(${100 / $bands}% - 1px) calc(${100 / $bands}%))`};
`;

const Bar = styled.div`
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: ${({ $left }) => $left}%;
  width: ${({ $width }) => $width}%;
  min-width: 6px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  padding: 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
`;

// Kısaltılmış ad: "Ayşe K."
function shortName(fullName) {
  const parts = (fullName || '').split(' ').filter(Boolean);
  if (parts.length <= 1) return fullName || '';
  return `${parts.slice(0, -1).join(' ')} ${parts[parts.length - 1][0]}.`;
}

export default function UpcomingTimelineCard({ slice, typeColors }) {
  const { t, i18n } = useTranslation();
  const data = slice.data;

  const fmtShort = (d) => d.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });

  const model = useMemo(() => {
    if (!data) return null;
    const windowStart = new Date(data.windowStart);
    windowStart.setHours(0, 0, 0, 0);
    const totalDays = data.days || 28;
    const bands = Math.max(1, Math.round(totalDays / 7));

    const bandLabels = Array.from({ length: bands }, (_, i) => {
      const s = new Date(windowStart.getTime() + i * 7 * DAY_MS);
      const e = new Date(windowStart.getTime() + (i * 7 + 6) * DAY_MS);
      return `${s.toLocaleDateString(i18n.language, { day: 'numeric' })}–${fmtShort(e)}`;
    });

    const byUser = new Map();
    (data.items || []).forEach((item) => {
      if (!byUser.has(item.userId)) {
        byUser.set(item.userId, {
          userId: item.userId,
          fullName: item.fullName,
          profilePicture: item.profilePicture,
          bars: [],
        });
      }
      const start = new Date(item.leaveStart); start.setHours(0, 0, 0, 0);
      const end = new Date(item.leaveEnd); end.setHours(0, 0, 0, 0); // exclusive return date
      const clampStart = Math.max(0, (start - windowStart) / DAY_MS);
      const clampEnd = Math.min(totalDays, (end - windowStart) / DAY_MS);
      if (clampEnd <= clampStart) return;
      byUser.get(item.userId).bars.push({
        key: item.leaveId,
        left: (clampStart / totalDays) * 100,
        width: ((clampEnd - clampStart) / totalDays) * 100,
        color: typeColors.get(item.leaveVariationId) || TYPE_FOLD_COLOR,
        department: item.department,
        tooltip: `${item.fullName} — ${item.leaveVariationName} (${fmtShort(new Date(item.leaveStart))} – ${fmtShort(new Date(new Date(item.leaveEnd).getTime() - DAY_MS))})`,
      });
    });

    const legend = new Map();
    (data.items || []).forEach((item) => {
      legend.set(item.leaveVariationId, {
        name: item.leaveVariationName,
        color: typeColors.get(item.leaveVariationId) || TYPE_FOLD_COLOR,
      });
    });

    return { bands, bandLabels, rows: [...byUser.values()], legend: [...legend.values()] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, typeColors, i18n.language]);

  const shownCount = data?.items?.length || 0;
  const remaining = (data?.totalCount || 0) - shownCount;

  return (
    <DashboardCard
      cardKey="yaklasan-izinler-zaman-tuneli"
      title={t('leaveDashboard.timeline.title')}
      meta={t('leaveDashboard.timeline.meta')}
      span={8}
      spanMd={6}
      loading={slice.loading}
      empty={!model || model.rows.length === 0}
      emptyText={t('leaveDashboard.timeline.empty')}
    >
      {model && (
        <div>
          <BandHeader>
            {model.bandLabels.map((label) => <BandLabel key={label}>{label}</BandLabel>)}
          </BandHeader>
          <Rows>
            {model.rows.map((row) => (
              <Row key={row.userId}>
                <RowLabel title={row.fullName}>
                  <RowAvatar
                    src={row.profilePicture}
                    name={row.fullName}
                    initials={initialsOf(row.fullName)}
                    size={20}
                  />
                  <span>{shortName(row.fullName)}</span>
                </RowLabel>
                <Track $bands={model.bands}>
                  {row.bars.map((bar) => (
                    <Bar
                      key={bar.key}
                      $left={bar.left}
                      $width={bar.width}
                      $color={bar.color}
                      title={bar.tooltip}
                    >
                      {bar.width > 12 ? bar.department : ''}
                    </Bar>
                  ))}
                </Track>
              </Row>
            ))}
          </Rows>
          <Legend>
            {model.legend.map((entry) => (
              <LegendItem key={entry.name} $color={entry.color}>{entry.name}</LegendItem>
            ))}
          </Legend>
          {remaining > 0 && <Footnote>{t('leaveDashboard.timeline.moreCount', { count: remaining })}</Footnote>}
        </div>
      )}
    </DashboardCard>
  );
}
